import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, format } from 'date-fns';
import { round } from 'lodash';
import { useRouter } from 'next/router';
import { Box, Typography, useTheme } from '@mui/material';
import { trackEvent } from '@surfline/web-common';
import { MapPinIcon } from '@wavetrak/theme';
import reportErrorToNewRelic from 'utils/reportErrorToNewRelic';

import { usePageCall } from 'common/hooks/usePageCall';
import { HISTORIC_DATE_FORMAT } from 'components/DatePickerCalendar/DatePickerCalendar';
import DatePickerMenu from 'components/DatePickerMenu/DatePickerMenu';
import DisclosureWithText from 'components/DisclosureWithText/DisclosureWithText';
import { ErrorMessage } from 'components/ErrorMessage/ErrorMessage';
import TideReportDay from 'components/TideReportDay/TideReportDay';
import config from 'config';
import ErrorPage from 'containers/ErrorPage/ErrorPage';
import useFetchSunlight from 'hooks/useFetchSunlight';
import useFetchTides from 'hooks/useFetchTides';
import createTideDayHourlyData from 'reducers/components/graphs/helpers/createTideDayHourlyData';
import { useUserPermissionStatus } from 'selectors/user';
import { GeoLocation } from 'types/locationView';
import { DisclosureDirection, TypographyType } from 'types/mui';
import type { TidePoint } from 'types/tide';
import forecastRangeSegmentDateProperties from 'utils/calculateSegmentDateProperties';
import createDateAtMidDay from 'utils/createDateAtMidDay';
import { oneDaySeconds } from 'utils/time';

import { useForecastDaysAllowed } from 'utils/forecastDaysAllowed';
import styles from './TideCharts.module.scss';
import { useTideLocationDistance } from './useTideLocationDistance';

interface TideChartsProps {
  spotId: string;
  spotName: string;
  spotGeo: GeoLocation;
  spotSlug: string;
  subregionId: string;
  subregionName: string;
  timezone: string;
  referrer: string;
}

const createDateToday = () => {
  const today = new Date();
  return format(today, HISTORIC_DATE_FORMAT);
};

const createDatesArray = (startDate: string, numberOfDays: number) =>
  [...Array(numberOfDays).keys()].map((index) => {
    const date = createDateAtMidDay(startDate);
    const newDate = addDays(date, index);
    return newDate.getTime() / 1000;
  });

const mapDaysToTimestampArray = (days: any) => days?.map((day: any) => day[0]?.timestamp);

const TideCharts: React.FC<TideChartsProps> = ({
  spotId,
  spotName,
  spotGeo,
  subregionId,
  spotSlug,
  subregionName,
  timezone,
  referrer,
}) => {
  const router = useRouter();
  const { hasExtendedTidesPermissions } = useUserPermissionStatus();

  const { forecastDaysAllowed } = useForecastDaysAllowed({
    type: 'spot',
    isEntitled: hasExtendedTidesPermissions,
  });

  const {
    palette: { text },
  } = useTheme();

  const [startDate, setStartDate] = useState<string>();
  const [showDatePickerCloseButton, setShowDatePickerCloseButton] = useState<boolean>(false);

  const {
    data: tideData,
    error: tidesError,
    loading: tidesIsLoading,
  } = useFetchTides(spotId, startDate);

  const {
    data: sunlightData,
    error: sunlightError,
    loading: sunlightIsLoading,
  } = useFetchSunlight(spotId, startDate);

  const [tideLocation, setTideLocation] = useState<string | undefined>(
    tideData?.associated.tideLocation.name,
  );

  const tide = useMemo(() => {
    if (!tidesIsLoading && tideData?.associated.tideLocation && tideData?.data.tides.length === 0) {
      reportErrorToNewRelic(new Error('Tide data missing'), 'TideCharts', {
        tideLocationName: tideData.associated.tideLocation.name,
      });
    }
    return {
      units: tideData?.associated.units,
      utcOffset: tideData?.associated.utcOffset,
      tideLocation: tideData?.associated.tideLocation,
      days: (tideData &&
        createTideDayHourlyData(tideData.data.tides, forecastDaysAllowed)) as TidePoint[][],
      loading: !tideData && !tidesError && tidesIsLoading,
      error: tidesError,
    };
  }, [tideData, forecastDaysAllowed, tidesError, tidesIsLoading]);

  const sunlightTimes = useMemo(
    () => ({
      location: sunlightData?.associated.location,
      utcOffset: sunlightData?.data.sunlight[0]?.midnightUTCOffset,
      days: sunlightData?.data.sunlight ?? [],
      loading: !sunlightData && !sunlightError && sunlightIsLoading,
      error: sunlightError,
    }),
    [sunlightData, sunlightError, sunlightIsLoading],
  );

  const { distanceFromSpot, distanceUnits } = useTideLocationDistance(
    spotGeo,
    tide?.tideLocation,
    tide?.units,
  );

  const [datesArray, setDatesArray] = useState<number[]>(
    mapDaysToTimestampArray(tide?.days) || createDatesArray(createDateToday(), forecastDaysAllowed),
  );

  const dateSelectionSegmentProperties = useMemo(
    () => ({
      spotId,
      spotName,
      subregionId,
      subregionName,
      pageName: 'Long Range Tide',
      uiElement: 'long-range-tide',
      action: 'click',
    }),
    [spotId, spotName, subregionId, subregionName],
  );

  const setNewStartDate = useCallback(
    (date: Date | null) => {
      const newStartDate = date && format(date, HISTORIC_DATE_FORMAT);
      if (newStartDate) {
        setShowDatePickerCloseButton(true);
        setStartDate(newStartDate);
        const segmentDateProperties = forecastRangeSegmentDateProperties({
          date,
          range: forecastDaysAllowed,
          timezone,
        });
        trackEvent('Forecast Range Updated', {
          ...dateSelectionSegmentProperties,
          ...segmentDateProperties,
        });
      }
    },
    [dateSelectionSegmentProperties, forecastDaysAllowed, timezone],
  );

  const resetStartDate = useCallback(() => {
    setShowDatePickerCloseButton(false);
    setStartDate(undefined);
  }, []);

  useEffect(() => {
    if (tideData && tideLocation !== tideData.associated.tideLocation.name) {
      setTideLocation(tideData?.associated.tideLocation.name);
    }
  }, [tideData, tideLocation]);

  useEffect(() => {
    const tideDays = mapDaysToTimestampArray(tide?.days);
    const daysLoading = startDate && createDatesArray(startDate, forecastDaysAllowed);
    const fallbackDays = createDatesArray(createDateToday(), forecastDaysAllowed);
    setDatesArray(tideDays || daysLoading || fallbackDays);
  }, [tide, startDate, forecastDaysAllowed]);

  const segmentProperties = useMemo(
    () => ({
      spotId,
      spotName,
      subregionId,
      category: 'forecast',
      path: router?.asPath,
      url: `${config.surflineHost}${router?.asPath}`,
      referrer,
    }),
    [referrer, router?.asPath, spotId, spotName, subregionId],
  );

  usePageCall('Long Range Tide', segmentProperties, !!router);

  const onClickHandler = useCallback(() => {
    router.push(`/surf-report/${spotSlug}/${spotId}`);
  }, [router, spotId, spotSlug]);

  const disclosureProps = useMemo(
    () => ({
      classnames: {
        disclosureButton: styles.disclosureButton,
        typographyText: styles.backToSpotText,
      },
      onClickHandler,
      disclosureColor: text.secondary,
      buttonText: 'Back to current forecast',
      direction: DisclosureDirection.LEFT,
      typographyVariant: 'overline1' as TypographyType,
      disclosureRotation: -90,
      clickableText: true,
      href: `/surf-report/${spotSlug}/${spotId}`,
    }),
    [onClickHandler, spotId, spotSlug, text.secondary],
  );

  const paywallSegmentProperties = useMemo(
    () => ({
      pageName: 'Long Range Tide',
      location: 'long range tide graph',
      category: 'forecast',
      spotId,
      spotName,
    }),
    [spotId, spotName],
  );

  const tideCharts = useMemo(
    () =>
      !tidesError && !sunlightError ? (
        datesArray.map((timestamp: number, index: number) => (
          <TideReportDay
            key={`day-${timestamp}`}
            tide={tide}
            sunlightTimes={sunlightTimes}
            daysToDisplay={[index]}
            startTimestamp={timestamp}
            endTimestamp={timestamp + oneDaySeconds}
            dayIndex={index}
            spotId={spotId}
            spotName={spotName}
          />
        ))
      ) : (
        <div className={styles.errorWrapper}>
          <ErrorMessage dataTestId="tides-error-message" />
        </div>
      ),
    [datesArray, spotId, spotName, sunlightError, sunlightTimes, tide, tidesError],
  );

  if (!tide.loading && !tide?.tideLocation?.name) {
    return <ErrorPage statusCode={404} />;
  }

  return (
    <Box className={styles.container}>
      <section className="sl-section-container" data-testid="tide-charts-page">
        <DisclosureWithText {...disclosureProps} />
        <div className={styles.headingWrapper}>
          <div>
            <Typography variant="title1" component="h1">
              {spotName} Tide Calendar
            </Typography>
          </div>
          {tideLocation && (
            <div className={styles.locationWrapper}>
              <MapPinIcon
                color={text.secondary}
                size={12}
                aria-label={`Location pin for ${tideData?.associated.tideLocation.name} || spot`}
                data-testid="tide-location-pin"
              />
              <Typography
                variant="callout1"
                component="h3"
                color="text.secondary"
                data-testid="tide-location-name"
              >
                {tideLocation}
                {distanceFromSpot && distanceFromSpot !== 0
                  ? ` • ${round(distanceFromSpot, 1)}${distanceUnits} away`
                  : null}
              </Typography>
            </div>
          )}
        </div>
        <div className={styles.datePickerContainer}>
          <DatePickerMenu
            onClickClearButton={resetStartDate}
            showCloseButton={showDatePickerCloseButton}
            closeButtonTooltipText="Return to current tide forecast"
            startDate={startDate}
            setStartDate={setNewStartDate}
            isPermitted={hasExtendedTidesPermissions}
            maxDate={new Date('2037-12-04')}
            minDate={new Date('2010-01-01')}
            segmentPropertiesCTA={paywallSegmentProperties}
            range={forecastDaysAllowed}
            selectButtonText="Select a forecast date"
            submitButtonText="View tides"
            enableFuture
          />
        </div>
        <div className="sl-section-full-bleed">
          <div className={styles.tideChartsContainer}>{tideCharts}</div>
        </div>
        <div className={styles.extendedTideDescription}>
          <Typography
            variant="title3"
            component="h2"
            className={styles.extendedTideDescriptionHeader}
          >
            About the tides for {spotName}
          </Typography>
          <Typography variant="body" className={styles.extendedTideDescriptionBody}>
            Get the tide tables and forecast for {spotName} with the tide port listed as{' '}
            {tideLocation}
            {distanceFromSpot && distanceFromSpot !== 0
              ? ` ${Math.round(distanceFromSpot)}${distanceUnits} away`
              : null}
            . Tide prediction accuracy varies depending on the distance from the spot to the tide
            port.
          </Typography>
        </div>
      </section>
    </Box>
  );
};

export default TideCharts;
